import React, { useState, useEffect } from 'react';
import { View, StyleSheet, Modal, Pressable, ScrollView, Linking, useWindowDimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ThemedText, SubHeading, Body, Caption } from '@/components/ui/Text';
import { PlaceRatingBadge } from '@/components/places/PlaceRatingBadge';
import { RatePlaceModal } from '@/components/places/RatePlaceModal';
import { getPlaceRating, generatePlaceId } from '@/services/placeRatings';
import { syncExternalRatings } from '@/services/externalPlaces';
import { VoteButton } from '@/components/collaboration/VoteButton';
import { AppImage } from '@/components/ui/AppImage';
import type { PlaceRatingAggregate } from '@/services/placeRatings';
import type { MapPin } from './MapView';
import type { ItineraryItem } from '@/services/itinerary';
import { formatPlaceCategoryLabel, type Place } from '@/services/places';
import type { MapReferencePoint, DistanceDisplayMode } from '@/types/mapReferences';
import { useLandmarkImage } from '@/hooks/useLandmarkImage';
import { buildExternalDirectionsUrl } from '@/utils/externalMapsQuery';

type PinDetailModalProps = {
  visible: boolean;
  pin: MapPin | null;
  onClose: () => void;
  onGetDirections?: (pin: MapPin) => void;
  onAddToItinerary?: (pin: MapPin) => void;
  referencePoints?: MapReferencePoint[];
  primaryReferencePoint?: MapReferencePoint;
  distanceDisplayMode?: DistanceDisplayMode;
  tripId?: string; // Optional trip ID for rating context
};

export const PinDetailModal: React.FC<PinDetailModalProps> = ({
  visible,
  pin,
  onClose,
  onGetDirections,
  onAddToItinerary,
  referencePoints = [],
  primaryReferencePoint,
  distanceDisplayMode = 'primary_reference',
  tripId,
}) => {
  const theme = useTheme();
  const { height: windowHeight } = useWindowDimensions();
  const detailScrollMaxHeight = Math.min(windowHeight * 0.58, 520);
  const [placeRating, setPlaceRating] = useState<PlaceRatingAggregate | null>(null);
  const [showRateModal, setShowRateModal] = useState(false);
  const [loadingRating, setLoadingRating] = useState(false);
  const [syncingWithGoogle, setSyncingWithGoogle] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);

  useEffect(() => {
    if (visible && pin) {
      loadPlaceRating();
    } else {
      setPlaceRating(null);
    }
  }, [visible, pin]);

  const loadPlaceRating = async () => {
    if (!pin) return;
    
    setLoadingRating(true);
    try {
      const placeId = generatePlaceId(pin.coordinates, pin.title);
      const rating = await getPlaceRating(placeId);
      setPlaceRating(rating);
      
      // Check if rating has Google sync data
      if (rating && (rating as any).lastSyncedAt) {
        const syncedAt = (rating as any).lastSyncedAt;
        setLastSyncedAt(syncedAt instanceof Date ? syncedAt : new Date(syncedAt));
      } else {
        setLastSyncedAt(null);
      }
    } catch (error) {
      console.error('[PinDetailModal] Error loading place rating:', error);
    } finally {
      setLoadingRating(false);
    }
  };

  const handleSyncWithGoogle = async () => {
    if (!pin) return;
    
    setSyncingWithGoogle(true);
    try {
      const placeId = generatePlaceId(pin.coordinates, pin.title);
      const updated = await syncExternalRatings(
        placeId,
        pin.coordinates,
        pin.title
      );
      
      if (updated) {
        setPlaceRating(updated);
        setLastSyncedAt(new Date());
      }
    } catch (error) {
      console.error('[PinDetailModal] Error syncing with Google:', error);
    } finally {
      setSyncingWithGoogle(false);
    }
  };

  if (!pin) return null;

  const handleGetDirections = () => {
    console.log('[PinDetailModal] Get Directions clicked for:', pin.title);
    if (onGetDirections) {
      console.log('[PinDetailModal] Calling onGetDirections handler');
      onGetDirections(pin);
      // Don't close the modal immediately - let the directions panel handle it
      // The user might want to see both
    } else {
      const url = buildExternalDirectionsUrl(pin, 'driving');
      if (url) {
        Linking.openURL(url).catch((err) => {
          console.error('[PinDetailModal] Error opening maps:', err);
        });
      }
      onClose();
    }
  };

  const handleAddToItinerary = () => {
    console.log('[PinDetailModal] Add to Itinerary clicked for:', pin.title);
    if (onAddToItinerary) {
      console.log('[PinDetailModal] Calling onAddToItinerary handler');
      onAddToItinerary(pin);
    } else {
      console.warn('[PinDetailModal] No onAddToItinerary handler available');
    }
    onClose();
  };

  const getPinIcon = () => {
    switch (pin.type) {
      case 'itinerary':
        return 'calendar';
      case 'landmark':
        return 'star';
      case 'user':
        return 'person';
      case 'search':
        return 'location';
      default:
        return 'pin';
    }
  };

  const getPinTypeLabel = () => {
    switch (pin.type) {
      case 'itinerary':
        return 'Itinerary Item';
      case 'landmark':
        return formatPlaceCategoryLabel((pin.data as Place | undefined)?.category);
      case 'user':
        return 'Your Location';
      case 'search':
        return 'Place';
      default:
        return 'Location';
    }
  };

  const getReferenceIcon = (type?: MapReferencePoint['type']): keyof typeof Ionicons.glyphMap => {
    switch (type) {
      case 'accommodation':
        return 'bed';
      case 'airport':
        return 'airplane';
      case 'train_station':
        return 'train';
      case 'meeting_point':
        return 'people';
      case 'custom':
        return 'pin';
      default:
        return 'navigate-outline';
    }
  };

  const itineraryItem = pin.data as ItineraryItem | undefined;
  const place = pin.data as Place | undefined;
  const { imageUrl: resolvedLandmarkImageUrl } = useLandmarkImage(
    pin.type === 'landmark' ? place?.name || pin.title : null,
    place?.imageUrl || null,
    {
      skip: pin.type !== 'landmark' || !visible,
      identityKey: place?.id || pin.id,
    },
  );
  const landmarkImageUrl = place?.imageUrl || resolvedLandmarkImageUrl || null;
  const referenceDistances = pin.distanceMeta?.referenceDistances;

  // Format start time for display (HH:mm -> 9:00 AM)
  const formatStartTime = (time?: string): string | null => {
    if (!time || time === 'flexible') return time === 'flexible' ? 'Flexible' : null;
    const [hours, minutes] = time.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return time;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const hour12 = hours % 12 || 12;
    return `${hour12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  const formattedStartTime =
    pin.type === 'itinerary' ? formatStartTime(itineraryItem?.startTime) : null;

  type DistanceEntry = {
    id: string;
    label: string;
    description: string;
    icon: keyof typeof Ionicons.glyphMap;
    color?: string;
    badge?: string;
  };

  const distanceEntries: DistanceEntry[] = [];

  if (referenceDistances && referenceDistances.length > 0) {
    referenceDistances.forEach((info, index) => {
      if (!info) {
        return;
      }
      const refPoint = info.referencePoint;
      distanceEntries.push({
        id: refPoint?.id ?? `reference-${index}`,
        label: info.distanceFormatted,
        description: refPoint?.title ?? 'Reference point',
        icon: getReferenceIcon(refPoint?.type),
        color: refPoint?.isPrimary ? theme.colors.primary : theme.colors.textSecondary,
        badge: info.walkable ? 'Walkable' : undefined,
      });
    });
  } else if (pin.distanceMeta?.distanceFormatted) {
    let description = 'Distance';
    let icon: keyof typeof Ionicons.glyphMap = 'navigate-outline';

    if (distanceDisplayMode === 'current_location') {
      description = 'From your location';
      icon = 'person-outline';
    } else if (distanceDisplayMode === 'previous_activity') {
      description = 'From previous activity';
      icon = 'map-outline';
    } else if (distanceDisplayMode === 'primary_reference' && primaryReferencePoint) {
      description = `From ${primaryReferencePoint.title}`;
      icon = getReferenceIcon(primaryReferencePoint.type);
    }

    distanceEntries.push({
      id: 'generic-distance',
      label: pin.distanceMeta.distanceFormatted,
      description,
      icon,
      color: pin.distanceMeta.distanceColor,
    });
  }

  const showReferencePointHint =
    !distanceEntries.length && referencePoints.length > 0 && primaryReferencePoint;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <Pressable style={[styles.overlay, { backgroundColor: theme.colors.modalOverlay }]} onPress={onClose}>
        <Pressable
          style={[
            styles.content,
            {
              backgroundColor: theme.colors.surface,
              borderTopLeftRadius: theme.radii.xl,
              borderTopRightRadius: theme.radii.xl,
            },
          ]}
          onPress={(e) => e.stopPropagation()}
        >
          {/* Drag handle */}
          <View style={[styles.dragHandle, { backgroundColor: theme.colors.borderMuted }]} />

          <View style={[styles.scrollArea, { height: detailScrollMaxHeight }]}>
            <ScrollView
              style={styles.scrollView}
              contentContainerStyle={styles.scrollContent}
              showsVerticalScrollIndicator={false}
            >
            {/* Header */}
            <View style={styles.header}>
              <View style={styles.headerLeft}>
                <View
                  style={[
                    styles.iconContainer,
                    { backgroundColor: theme.colors.primary + '15' },
                  ]}
                >
                  <Ionicons
                    name={getPinIcon()}
                    size={24}
                    color={theme.colors.primary}
                  />
                </View>
                <View style={styles.headerText}>
                  <SubHeading style={styles.title}>{pin.title}</SubHeading>
                  <Caption color={theme.colors.textSecondary}>
                    {getPinTypeLabel()}
                  </Caption>
                </View>
              </View>
              <Pressable onPress={onClose} style={styles.closeButton}>
                <Ionicons
                  name="close"
                  size={24}
                  color={theme.colors.textSecondary}
                />
              </Pressable>
            </View>

            {pin.type === 'landmark' && landmarkImageUrl ? (
              <View
                style={{
                  marginBottom: 12,
                  borderRadius: theme.radii.lg,
                  overflow: 'hidden',
                  borderWidth: StyleSheet.hairlineWidth,
                  borderColor: theme.colors.borderMuted,
                }}
              >
                <AppImage
                  source={{ uri: landmarkImageUrl }}
                  style={{ width: '100%', height: 170, backgroundColor: theme.colors.borderMuted }}
                  resizeMode="cover"
                  cachePolicy="memory-disk"
                />
              </View>
            ) : null}

            {/* Badges */}
            {pin.badges && pin.badges.length > 0 && (
              <View style={styles.badgesContainer}>
                {pin.badges.map((badge, index) => (
                  <View
                    key={index}
                    style={[
                      styles.badge,
                      {
                        backgroundColor: theme.colors.primary + '15',
                      },
                    ]}
                  >
                    <Caption color={theme.colors.primary}>{badge}</Caption>
                  </View>
                ))}
              </View>
            )}

            {/* Start Time (for itinerary items) */}
            {pin.type === 'itinerary' && formattedStartTime && (
              <View style={styles.section}>
                <View style={styles.sectionRow}>
                  <Ionicons
                    name="time-outline"
                    size={16}
                    color={theme.colors.primary}
                  />
                  <Body color={theme.colors.text} style={styles.subtitle}>
                    {formattedStartTime}
                  </Body>
                </View>
              </View>
            )}

            {/* Subtitle */}
            {pin.subtitle && (
              <View style={styles.section}>
                <View style={styles.sectionRow}>
                  <Ionicons
                    name="location-outline"
                    size={16}
                    color={theme.colors.textSecondary}
                  />
                  <Body color={theme.colors.textSecondary} style={styles.subtitle}>
                    {pin.subtitle}
                  </Body>
                </View>
              </View>
            )}

            {/* Distance Overview */}
            {distanceEntries.length > 0 && (
              <View style={styles.section}>
                <View style={styles.sectionHeaderRow}>
                  <Body style={styles.sectionTitle}>Distance overview</Body>
                  {primaryReferencePoint && (
                    <Caption color={theme.colors.textSecondary}>
                      Primary: {primaryReferencePoint.title}
                    </Caption>
                  )}
                </View>
                <View style={styles.distanceList}>
                  {distanceEntries.map((entry) => (
                    <View key={entry.id} style={styles.distanceRow}>
                      <View
                        style={[
                          styles.distanceIconContainer,
                          entry.color ? { borderColor: entry.color } : null,
                        ]}
                      >
                        <Ionicons
                          name={entry.icon}
                          size={16}
                          color={entry.color || theme.colors.text}
                        />
                      </View>
                      <View style={styles.distanceTextContainer}>
                        <Body style={styles.distanceValue}>{entry.label}</Body>
                        <Caption color={theme.colors.textSecondary}>
                          {entry.description}
                          {entry.badge ? ` • ${entry.badge}` : ''}
                        </Caption>
                      </View>
                    </View>
                  ))}
                </View>
              </View>
            )}

            {showReferencePointHint && (
              <View style={styles.section}>
                <Caption color={theme.colors.textSecondary}>
                  Add coordinates to this item to see distance from {primaryReferencePoint?.title}.
                </Caption>
              </View>
            )}

            {/* Itinerary Item Details */}
            {itineraryItem && (
              <View style={styles.section}>
                {itineraryItem.startDate && (
                  <View style={styles.sectionRow}>
                    <Ionicons
                      name="time-outline"
                      size={16}
                      color={theme.colors.textSecondary}
                    />
                    <Body color={theme.colors.textSecondary}>
                      {new Date(itineraryItem.startDate).toLocaleString()}
                    </Body>
                  </View>
                )}
                {itineraryItem.category && (
                  <View style={styles.sectionRow}>
                    <Ionicons
                      name="pricetag-outline"
                      size={16}
                      color={theme.colors.textSecondary}
                    />
                    <Body color={theme.colors.textSecondary}>
                      {itineraryItem.category}
                    </Body>
                  </View>
                )}
                {itineraryItem.description && (
                  <Body style={styles.description}>{itineraryItem.description}</Body>
                )}
              </View>
            )}

            {/* Place Details */}
            {place && (
              <View style={styles.section}>
                {place.address && (
                  <View style={styles.sectionRow}>
                    <Ionicons
                      name="location-outline"
                      size={16}
                      color={theme.colors.textSecondary}
                    />
                    <Body color={theme.colors.textSecondary} style={styles.flex1}>
                      {place.address}
                    </Body>
                  </View>
                )}
                {/* Community Rating */}
                {placeRating && (
                  <View style={styles.ratingSection}>
                    <View style={styles.ratingHeader}>
                      <View style={styles.ratingTitleRow}>
                        <Body style={styles.ratingLabel}>Community Rating</Body>
                        {lastSyncedAt && (
                          <View style={styles.syncedBadge}>
                            <Ionicons name="logo-google" size={12} color={theme.colors.primary} />
                            <Caption style={[styles.syncedText, { color: theme.colors.primary }]}>
                              Synced
                            </Caption>
                          </View>
                        )}
                      </View>
                      <PlaceRatingBadge
                        rating={placeRating.averageRating}
                        totalRatings={placeRating.totalRatings}
                        size="medium"
                        showCount={true}
                      />
                    </View>
                  </View>
                )}
                
                {/* External Rating (fallback) */}
                {!placeRating && place.rating && (
                  <View style={styles.sectionRow}>
                    <Ionicons
                      name="star"
                      size={16}
                      color={theme.colors.warning || '#FFA500'}
                    />
                    <Body color={theme.colors.textSecondary}>
                      {place.rating.toFixed(1)} / 5.0
                    </Body>
                  </View>
                )}
                {place.description && (
                  <Body style={styles.description}>{place.description}</Body>
                )}
                
                {/* Voting Section */}
                {tripId && (pin.type === 'landmark' || pin.type === 'search') && (
                  <View style={[styles.section, styles.sectionCard, { borderColor: theme.colors.borderMuted }]}>
                    <View style={styles.voteHeaderRow}>
                      <Caption color={theme.colors.textSecondary} style={styles.voteTitle}>
                        Group vote on this place
                      </Caption>
                      <VoteButton
                        tripId={tripId}
                        itemId={generatePlaceId(pin.coordinates, pin.title)}
                        itemType="place"
                        size="small"
                        showCounts={true}
                        orientation="horizontal"
                      />
                    </View>
                  </View>
                )}
              </View>
            )}
            
            {/* Rate This Place Button */}
            {(pin.type === 'landmark' || pin.type === 'search') && (
              <View style={[styles.section, styles.sectionCard, { borderColor: theme.colors.borderMuted }]}>
                <Button
                  label={placeRating ? 'Update Rating' : 'Rate This Place'}
                  variant="secondary"
                  icon={<Ionicons name="star-outline" size={18} color={theme.colors.primary} />}
                  onPress={() => setShowRateModal(true)}
                />
                
                {/* Sync with Google Button */}
                <Button
                  label={syncingWithGoogle ? 'Syncing...' : 'Sync with Google'}
                  variant="outlined"
                  icon={
                    syncingWithGoogle
                      ? undefined
                      : <Ionicons name="logo-google" size={18} color={theme.colors.primary} />
                  }
                  onPress={handleSyncWithGoogle}
                  disabled={syncingWithGoogle}
                  loading={syncingWithGoogle}
                />
              </View>
            )}

            </ScrollView>
          </View>

          {/* Rate Place Modal */}
          <RatePlaceModal
            visible={showRateModal}
            onClose={() => setShowRateModal(false)}
            place={{
              name: pin.title,
              coordinates: pin.coordinates,
              category: place?.category,
              address: place?.address || pin.subtitle,
            }}
            tripId={tripId}
            onRated={() => {
              loadPlaceRating(); // Refresh rating after submission
            }}
          />

          {/* Actions */}
          <View style={[styles.actions, { borderTopColor: theme.colors.borderMuted }]}>
            {pin.type !== 'user' && onAddToItinerary && (
              <Button
                label="Add to Itinerary"
                variant="secondary"
                onPress={handleAddToItinerary}
                style={styles.actionButton}
                icon={<Ionicons name="add-circle-outline" size={20} color={theme.colors.primary} />}
              />
            )}
            <Button
              label="Get Directions"
              onPress={handleGetDirections}
              style={styles.actionButton}
              icon={<Ionicons name="navigate-outline" size={20} color={theme.colorScheme === 'dark' ? '#000000' : theme.colors.surface} />}
            />
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    // Note: backgroundColor is set inline with theme.colors.modalOverlay
    justifyContent: 'flex-end',
  },
  content: {
    maxHeight: '80%',
    width: '100%',
    paddingBottom: 0,
  },
  dragHandle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    alignSelf: 'center',
    marginTop: 12,
    marginBottom: 8,
  },
  scrollArea: {
    width: '100%',
    flexShrink: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    gap: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerText: {
    flex: 1,
    gap: 4,
  },
  title: {
    margin: 0,
    fontSize: 20,
  },
  closeButton: {
    padding: 4,
  },
  badgesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  sectionHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionTitle: {
    fontWeight: '600',
  },
  distanceList: {
    marginTop: 8,
    gap: 10,
  },
  distanceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  distanceIconContainer: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.5,
    borderColor: '#E0E0E0',
  },
  distanceTextContainer: {
    flex: 1,
    gap: 2,
  },
  distanceValue: {
    fontWeight: '600',
  },
  section: {
    gap: 8,
  },
  sectionCard: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 10,
  },
  voteHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  voteTitle: {
    fontSize: 12,
    flex: 1,
  },
  sectionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  flex1: {
    flex: 1,
  },
  subtitle: {
    flex: 1,
  },
  description: {
    marginTop: 4,
    lineHeight: 20,
  },
  actions: {
    flexDirection: 'row',
    gap: 12,
    padding: 16,
    paddingTop: 12,
    borderTopWidth: 1,
    // Note: borderTopColor is set inline with theme.colors.borderMuted
  },
  actionButton: {
    flex: 1,
  },
  ratingSection: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.1)',
  },
  ratingHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
  },
  ratingLabel: {
    fontSize: 14,
    fontWeight: '600',
  },
  ratingTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  syncedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
    backgroundColor: 'rgba(0,0,0,0.05)',
  },
  syncedText: {
    fontSize: 10,
    fontWeight: '600',
  },
});

